import * as R from "runtypes";
import { z } from "zod";

import { typedObjectKeys } from "../../utils/typedObjects.js";
import { zFromRuntype } from "../../zod/utils.js";

import { RuntypeOrZod } from "./types.js";

/**
 * Runtypes are necessary to define each entity's configuration; this is a utility function to convert an enum to a runtype!
 */
export const convertEnumToRuntype = <T extends string>(
  enumObj: Record<T, T>,
): R.Union<[R.Literal<T>, ...R.Literal<T>[]]> => {
  const [first, ...rest] = typedObjectKeys(enumObj);
  return R.Union<[R.Literal<T>, ...R.Literal<T>[]]>(
    R.Literal(first!),
    // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- https://github.com/lukastaegert/eslint-plugin-tree-shaking/issues/71
    ...rest.map((k) => R.Literal(k)),
  );
};

/**
 * Coerces a field spec entry to a zod schema so payload schemas can be derived from a
 * `Config` whose fields are a mix of runtypes and zod.
 *
 * A runtype is wrapped rather than reimplemented, so there is still only one copy of
 * each field's validation.
 */
export const toZodSchema = (field: RuntypeOrZod): z.ZodTypeAny =>
  field instanceof z.ZodType ? field : zFromRuntype(field);
